import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { UserCog, ChevronDown, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

/**
 * RunAsSelector - Dropdown for Super Admin to start "Run As" mode
 * 
 * In the new architecture:
 * - Only shown when isSuperAdmin() (runningUser is super_admin)
 * - Only shown when NOT already in Run As mode
 * - The banner display is handled by Header.jsx
 */
const RunAsSelector = () => {
  const { user, token, isSuperAdmin, isRunAsMode, startRunAs, getAuthHeaders } = useAuth();
  const [calendarAdmins, setCalendarAdmins] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only fetch when Super Admin and not in Run As mode
    if (isSuperAdmin() && !isRunAsMode()) {
      fetchCalendarAdmins();
    }
  }, [user]);

  const fetchCalendarAdmins = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/auth/run-as/calendar-admins`, {
        headers: getAuthHeaders()
      });
      setCalendarAdmins(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch calendar admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartRunAs = (admin) => {
    // Pass role along with user info for the new architecture
    startRunAs({
      id: admin.id,
      role: 'calendar_admin',
      full_name: admin.full_name,
      email: admin.email
    }, window.location.pathname);
    
    setShowDropdown(false);
    // Navigate to dashboard with new context
    window.location.href = '/dashboard';
  };

  // Only show for Super Admins who are NOT in Run As mode
  // (The Run As banner is shown in Header.jsx when in Run As mode)
  if (!isSuperAdmin() || isRunAsMode()) return null;

  // Show "Start Run As" dropdown for Super Admin
  return (
    <div className="relative">
      <button
        onClick={() => {
          setShowDropdown(!showDropdown);
          if (!showDropdown && calendarAdmins.length === 0) {
            fetchCalendarAdmins();
          }
        }}
        className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-md text-sm font-medium text-gray-700 transition-colors"
        data-testid="run-as-button"
      >
        <UserCog className="h-4 w-4" />
        <span>Run As</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
      </button>

      {showDropdown && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowDropdown(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Select Calendar Admin
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Work within a Calendar Admin's context
              </p>
            </div>
            
            <div className="max-h-64 overflow-y-auto">
              {loading ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  Loading...
                </div>
              ) : calendarAdmins.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  No Calendar Admins found
                </div>
              ) : (
                calendarAdmins.map((admin) => (
                  <button
                    key={admin.id}
                    onClick={() => handleStartRunAs(admin)}
                    className="w-full px-4 py-3 text-left hover:bg-yellow-50 border-b border-gray-100 last:border-b-0 transition-colors"
                    data-testid={`run-as-admin-${admin.id}`}
                  >
                    <div className="font-medium text-gray-900">{admin.full_name}</div>
                    <div className="text-sm text-gray-500">{admin.email}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {admin.calendar_count} calendar{admin.calendar_count !== 1 ? 's' : ''}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RunAsSelector;
